import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Ruler } from "lucide-react";
import { PasswordInput, PasswordChecklist, passwordValid } from "../components/PasswordInput";

export default function Register() {
    const { setUser } = useAuth();
    const { t } = useI18n();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/register", { name, email, password });
            setUser(data);
            navigate("/dashboard");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0b0f17] text-slate-100 cad-grid p-6">
            <div className="w-full max-w-sm fade-in-up bg-[#111827]/90 backdrop-blur-md border border-[#1e293b] rounded-2xl p-8 shadow-2xl">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                        <Ruler className="w-5 h-5 text-white" />
                    </div>
                    <div className="font-mono font-bold text-lg">BLIND<span className="text-blue-500">CAD</span></div>
                </div>
                <h2 className="text-2xl font-mono font-bold">{t("auth_reg_title")}</h2>
                <p className="text-slate-400 text-sm mt-1">{t("auth_reg_sub")}</p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <Label htmlFor="name" className="text-slate-300">{t("auth_name")}</Label>
                        <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)}
                            className="mt-1.5 bg-[#0b0f17] border-[#1e293b] text-slate-100" placeholder={t("auth_name_ph")} />
                    </div>
                    <div>
                        <Label htmlFor="email" className="text-slate-300">{t("auth_email")}</Label>
                        <Input id="email" name="email" autoComplete="username" data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            required className="mt-1.5 bg-[#0b0f17] border-[#1e293b] text-slate-100" placeholder="name@company.com" />
                    </div>
                    <div>
                        <Label htmlFor="password" className="text-slate-300">{t("auth_password")}</Label>
                        <div className="mt-1.5">
                            <PasswordInput id="password" name="new-password" autoComplete="new-password" testId="register-password-input" value={password}
                                onChange={(e) => setPassword(e.target.value)} required minLength={8}
                                className="bg-[#0b0f17] border-[#1e293b] text-slate-100" placeholder={t("auth_pw_ph")} />
                        </div>
                    </div>
                    <PasswordChecklist password={password} />
                    {error && <div data-testid="register-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2">{error}</div>}
                    <Button data-testid="register-submit-button" type="submit" disabled={loading || !passwordValid(password)}
                        className="w-full bg-blue-600 hover:bg-blue-500 text-white">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth_register_btn")}
                    </Button>
                </form>
                <p className="mt-6 text-center text-sm text-slate-400">
                    {t("auth_have_account")} <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium">{t("auth_login_link")}</Link>
                </p>
            </div>
        </div>
    );
}
